<template>
  <div class="email-unsubscribe-page d-flex align-items-center justify-content-center min-vh-100 bg-light">
    <div class="card shadow-sm" style="max-width: 500px; width: 100%;">
      <div class="card-body p-4">
        <!-- Header -->
        <div class="text-center mb-4">
          <h4 class="mb-1">Email Preferences</h4>
          <p class="text-muted small">Manage which emails you receive from Visual Crossing</p>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted mt-2">Loading your preferences...</p>
        </div>

        <!-- Error state -->
        <div v-else-if="loadError" class="text-center py-4">
          <i class="bi bi-exclamation-triangle text-warning" style="font-size: 2rem;"></i>
          <p class="text-danger mt-2">{{ loadError }}</p>
          <p class="text-muted small">If you continue to have problems, please contact
            <a href="/support/">Support</a>.
          </p>
        </div>

        <!-- Preferences form -->
        <div v-else>
          <div class="form-check mb-3">
            <input class="form-check-input" type="checkbox" id="unsub-pref-service" checked disabled />
            <label class="form-check-label text-muted" for="unsub-pref-service">
              Service & account notifications
            </label>
            <div class="form-text">Required notifications about your account and service status.</div>
          </div>

          <div class="form-check mb-3">
            <input class="form-check-input" type="checkbox" id="unsub-pref-news"
                   v-model="emailPrefs.news_and_announcements" />
            <label class="form-check-label" for="unsub-pref-news">
              News &amp; announcements
            </label>
            <div class="form-text">Product updates, new features, and educational content.</div>
          </div>

          <div class="form-check mb-3">
            <input class="form-check-input" type="checkbox" id="unsub-pref-marketing"
                   v-model="emailPrefs.marketing" />
            <label class="form-check-label" for="unsub-pref-marketing">
              Marketing &amp; promotions
            </label>
            <div class="form-text">Special offers, tips, and promotional content.</div>
          </div>

          <hr />

          <!-- Save button -->
          <div class="d-flex align-items-center">
            <button class="btn btn-primary" :disabled="saving" @click="savePreferences">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status"></span>
              {{ saving ? 'Saving...' : 'Save Preferences' }}
            </button>

            <span v-if="saved" class="text-success ms-3 small">
              <i class="bi bi-check-circle"></i> Preferences saved
            </span>
            <span v-if="saveError" class="text-danger ms-3 small">
              {{ saveError }}
            </span>
          </div>

          <!-- Unsubscribe all shortcut -->
          <div class="mt-3 pt-3 border-top">
            <a href="#" class="text-muted small" @click.prevent="unsubscribeAll">
              Unsubscribe from all optional emails
            </a>
          </div>
        </div>

        <!-- Footer -->
        <div class="text-center mt-4 pt-3 border-top">
          <p class="text-muted small mb-1">Visual Crossing Corporation, Herndon, VA 20171, USA</p>
          <p class="text-muted small mb-0">
            <a href="https://www.visualcrossing.com/privacy" class="text-muted">Privacy Policy</a> &bull;
            <a href="https://www.visualcrossing.com" class="text-muted">Visual Crossing</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {VcGlobal} from '/src/visualcrossing-core/vcglobal_conn';

export default {
  name: 'EmailUnsubscribe',
  data() {
    return {
      userId: null,
      campaignId: null,
      loading: true,
      loadError: null,
      saving: false,
      saved: false,
      saveError: null,
      emailPrefs: {
        news_and_announcements: true,
        marketing: true
      }
    };
  },
  mounted() {
    // Read u (userId/groupId) and c (campaignId) from URL query params
    const params = new URLSearchParams(window.location.search);
    this.userId = params.get('u');
    this.campaignId = params.get('c');

    if (!this.userId) {
      this.loading = false;
      this.loadError = 'Missing user identifier. Please use the link from your email.';
      return;
    }

    this.loadPreferences();
  },
  methods: {
    getServiceUrl(endpoint) {
      var serverRoot = VcGlobal.SERVER_ROOT || '';
      return serverRoot + '/VisualCrossingWebServices/rest/services/' + endpoint;
    },

    loadPreferences() {
      var me = this;
      me.loading = true;
      me.loadError = null;

      var url = me.getServiceUrl('emailprefsnoauth') + '?u=' + encodeURIComponent(me.userId);
      if (me.campaignId) {
        url += '&c=' + encodeURIComponent(me.campaignId);
      }

      fetch(url)
        .then(function(response) {
          if (!response.ok) throw new Error('Failed to load preferences');
          return response.json();
        })
        .then(function(data) {
          me.loading = false;
          if (data && data.errorCode) {
            me.loadError = data.message || 'Could not load your preferences.';
            return;
          }
          if (data && data.preferences) {
            me.emailPrefs.news_and_announcements = data.preferences.news_and_announcements !== false;
            me.emailPrefs.marketing = data.preferences.marketing !== false;
          }
        })
        .catch(function(error) {
          me.loading = false;
          me.loadError = 'Could not load your preferences. Please try again later.';
        });
    },

    savePreferences() {
      var me = this;
      me.saving = true;
      me.saved = false;
      me.saveError = null;

      var payload = {
        userId: me.userId,
        campaignId: me.campaignId,
        preferences: me.emailPrefs
      };

      fetch(me.getServiceUrl('saveemailprefsnoauth'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function(response) {
          if (!response.ok) throw new Error('Failed to save preferences');
          return response.json();
        })
        .then(function(data) {
          me.saving = false;
          if (data && data.errorCode) {
            me.saveError = data.message || 'Could not save preferences.';
          } else {
            me.saved = true;
            setTimeout(function() { me.saved = false; }, 5000);
          }
        })
        .catch(function(error) {
          me.saving = false;
          me.saveError = 'Could not save preferences. Please try again.';
        });
    },

    unsubscribeAll() {
      this.emailPrefs.news_and_announcements = false;
      this.emailPrefs.marketing = false;
      this.savePreferences();
    }
  }
};
</script>

<style scoped>
.email-unsubscribe-page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}
</style>
